/**
 * Instagram Feed Post Mockup
 * Simulates how an ad appears in the Instagram feed
 */

import React from 'react';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal } from 'lucide-react';

interface InstagramFeedMockupProps {
  children: React.ReactNode;
  adWidth: number;
  adHeight: number;
}

export function InstagramFeedMockup({ children, adWidth, adHeight }: InstagramFeedMockupProps) {
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-lg w-full max-w-[470px]">
      {/* Post header */}
      <div className="p-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 p-[2px]">
          <div className="w-full h-full rounded-full bg-white p-[2px]">
            <div className="w-full h-full rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <span className="text-white font-bold text-xs">M</span>
            </div>
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-1">
            <span className="font-semibold text-[#262626] text-sm">mooncloak</span>
            <svg className="w-3.5 h-3.5 text-[#0095F6]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="text-xs text-[#8E8E8E]">Sponsored</div>
        </div>
        <button className="p-1">
          <MoreHorizontal className="w-5 h-5 text-[#262626]" />
        </button>
      </div>

      {/* Ad image */}
      <div className="w-full">
        {children}
      </div>

      {/* Action buttons */}
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button>
            <Heart className="w-6 h-6 text-[#262626]" />
          </button>
          <button>
            <MessageCircle className="w-6 h-6 text-[#262626]" />
          </button>
          <button>
            <Send className="w-6 h-6 text-[#262626]" />
          </button>
        </div>
        <button>
          <Bookmark className="w-6 h-6 text-[#262626]" />
        </button>
      </div>

      {/* Likes */}
      <div className="px-3 pb-1">
        <span className="text-sm font-semibold text-[#262626]">12,847 likes</span>
      </div>

      {/* Caption */}
      <div className="px-3 pb-2">
        <span className="text-sm">
          <span className="font-semibold text-[#262626]">mooncloak</span>
          {' '}
          <span className="text-[#262626]">Your privacy matters. Take control today.</span>
        </span>
      </div>

      {/* CTA button */}
      <div className="px-3 pb-3">
        <button className="w-full py-2 bg-[#0095F6] text-white text-sm font-semibold rounded-lg hover:bg-[#1877F2] transition-colors">
          Learn More
        </button>
      </div>

      {/* Timestamp */}
      <div className="px-3 pb-3">
        <span className="text-[10px] text-[#8E8E8E] uppercase">2 hours ago</span>
      </div>
    </div>
  );
}
